import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Form } from "react-bootstrap";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import MplacerProductsTableComponent from "../../components/tables/Mplacer/MplacerProductsTableComponent";
import LabelFieldComponent from "../../components/fields/LabelFieldComponent";
import PageLayout from "../../layouts/PageLayout";

export default function UserProductList() {
  const { t, n } = useContext(TranslatorContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active Tab: 'new' | 'marketplaced' | 'sold' | 'pending'
  const [activeTab, setActiveTab] = useState("new");

  const [allProducts, setAllProducts] = useState([]);

  // Filter States
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  // Dropdown Data States
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [counts, setCounts] = useState({
    new: 0,
    marketplaced: 0,
    sold: 0,
    pending: 0,
  });

  const tableHeaders = [
    "Product Code",
    "Title",
    "Category",
    "Brand",
    "Selling Style",
    "Buying Price",
    "Quantity",
    "Marketplaces",
    "Action",
  ];

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      Accept: "application/json",
    },
  });

  const extractData = (res) => {
    if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get("tab");
    if (
      tabParam &&
      ["new", "marketplaced", "sold", "pending"].includes(tabParam)
    ) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Tab Tab change hone par status filter reset karein
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setSelectedStatus("");
    navigate(`/mplacer-products?tab=${tabKey}`);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/market-placer/initial-data`,
          getAuthHeader()
        );
        const resourceData = response.data?.data || {};

        if (Array.isArray(resourceData.product_categories)) {
          setCategories(resourceData.product_categories);
        }
        if (Array.isArray(resourceData.brands)) {
          setBrands(resourceData.brands);
        }
      } catch (err) {
        console.error("Error fetching initial dropdown data:", err);
      }
    };

    fetchInitialData();
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const [newRes, marketplacedRes, soldRes, pendingRes] = await Promise.all([
        axios.get(`${baseUrl}/market-placer/products`, getAuthHeader()),
        axios.get(`${baseUrl}/market-placer/market-placed/products`, getAuthHeader()),
        axios.get(`${baseUrl}/market-placer/sold-products`, getAuthHeader()),
        axios.get(`${baseUrl}/market-placer/pending-sold-products`, getAuthHeader()),
      ]);

      setCounts({
        new: extractData(newRes).length,
        marketplaced: extractData(marketplacedRes).length,
        sold: extractData(soldRes).length,
        pending: extractData(pendingRes).length,
      });
    } catch (err) {
      console.error("Error fetching market-placer counts:", err);
    }
  }, []);

  const fetchTabData = useCallback(async (tab) => {
    setLoading(true);
    setError(null);

    let endpoint = "";
    if (tab === "new") endpoint = "/market-placer/products";
    else if (tab === "marketplaced") endpoint = "/market-placer/market-placed/products";
    else if (tab === "sold") endpoint = "/market-placer/sold-products";
    else if (tab === "pending") endpoint = "/market-placer/pending-sold-products";

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        getAuthHeader()
      );

      const listData = extractData(response);
      setAllProducts(listData);
    } catch (err) {
      console.error("Error fetching tab data:", err);
      setError(
        err.response?.data?.message || "Failed to fetch products. Please try again."
      );
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  // Dynamically calculate status options available in current table data
  const availableStatuses = useMemo(() => {
    const statuses = new Set();
    allProducts.forEach((product) => {
      if (Array.isArray(product.marketPlace)) {
        product.marketPlace.forEach((mp) => {
          const status = mp?.status || (mp?.showing === false || mp?.showing === 0 ? "Sold" : "Unsold");
          if (status) {
            statuses.add(status);
          }
        });
      }
    });
    return Array.from(statuses);
  }, [allProducts]);

  // Client-side filtering strictly for current active tab's dataset
  const filteredProducts = useMemo(() => {
    return allProducts.filter((product) => {
      // 1. Search Filter
      const matchesSearch =
        !search ||
        product.title?.toLowerCase().includes(search.toLowerCase()) ||
        product.product_code?.toLowerCase().includes(search.toLowerCase());

      // 2. Category Filter
      const productCatId = product.category?.id || product.category_id;
      const matchesCategory =
        !selectedCategory || String(productCatId) === String(selectedCategory);

      // 3. Brand Filter
      const productBrandId = product.brand?.id || product.brand_id;
      const matchesBrand =
        !selectedBrand || String(productBrandId) === String(selectedBrand);

      // 4. Status Filter
      let matchesStatus = true;
      if (selectedStatus && Array.isArray(product.marketPlace)) {
        matchesStatus = product.marketPlace.some((mp) => {
          const status = mp?.status || (mp?.showing === false || mp?.showing === 0 ? "Sold" : "Unsold");
          return status.toLowerCase() === selectedStatus.toLowerCase();
        });
      }

      return matchesSearch && matchesCategory && matchesBrand && matchesStatus;
    });
  }, [allProducts, search, selectedCategory, selectedBrand, selectedStatus]);

  const cardsData = [
    {
      key: "new",
      title: "New Products",
      digit: counts.new,
      icon: "shopping_bag",
      variant: activeTab === "new" ? "lg blue active-card" : "lg blue",
    },
    {
      key: "marketplaced",
      title: "Marketplaced Products",
      digit: counts.marketplaced,
      icon: "storefront",
      variant: activeTab === "marketplaced" ? "lg purple active-card" : "lg purple",
    },
    {
      key: "sold",
      title: "Sold Products",
      digit: counts.sold,
      icon: "sell",
      variant: activeTab === "sold" ? "lg green active-card" : "lg green",
    },
    {
      key: "pending",
      title: "Pending Sold",
      digit: counts.pending,
      icon: "pending_actions",
      variant: activeTab === "pending" ? "lg red active-card" : "lg red",
    },
  ];

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card mb-4">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                {t("product_list")} ({filteredProducts.length})
              </h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("home")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("products")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">{t("product_list")}</li>
              </ul>
            </div>
          </div>
        </Col>

        {cardsData.map((card) => (
          <Col key={card.key} sm={6} lg={3}>
            <div
              style={{ cursor: "pointer" }}
              onClick={() => handleTabChange(card.key)}
            >
              <FloatCardComponent
                variant={card.variant}
                digit={n ? n(card.digit) : card.digit}
                title={card.title}
                icon={card.icon}
              />
            </div>
          </Col>
        ))}

        <Col xl={12} className="mt-4">
          <div className="mc-card">
            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}

            {/* Filter Controls Row */}
            <Row className="g-3 align-items-end">
              {/* Search Filter */}


              {/* Category Filter */}
              <Col md={3} xl={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">
                    Category
                  </Form.Label>
                  <Form.Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={{ height: "45px" }}
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Brand Filter */}
              <Col md={3} xl={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">
                    Brand
                  </Form.Label>
                  <Form.Select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    style={{ height: "45px" }}
                  >
                    <option value="">All Brands</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {activeTab !== "new" && (

                < Col md={3} xl={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">
                      Status
                    </Form.Label>
                    <Form.Select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      style={{ height: "45px" }}
                    >
                      <option value="">All Statuses</option>
                      {availableStatuses.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              <Col md={3} xl={3}>
                <LabelFieldComponent
                  type="search"
                  placeholder="Search by Code or Title..."
                  label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>

              {/* Products Table */}
              <Col xl={12} className="mt-4">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_products...")}</p>
                  </div>
                ) : (
                  <MplacerProductsTableComponent
                    thead={tableHeaders}
                    tbody={filteredProducts}
                    activeTab={activeTab}
                    onRefresh={() => fetchTabData(activeTab)}
                  />
                )}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </PageLayout >
  );
}